import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import AdvanceFileInput from 'components/field/AdvanceFileInput';
import Button from 'components/field/Button';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Alert from 'components/global/Alert';
import Radio from 'components/global/Radio';
import Typography from 'components/global/Typography';
import { ModalActions, ModalTitle } from 'components/global/styled/Modal.styled';

import useAccessControl from 'hooks/useAccessControl';
import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import { usePrompt } from 'hooks/usePrompt';

import { COMPANY } from 'services/consts';

import { setUserGeneralSettings } from 'stores/userSlice';

import AppLogger from 'utils/AppLogger';
import { ACCOUNT_TYPES } from 'utils/constants';

import * as S from './VerifyCR.styled';

const validateOptions = {
	shouldValidate: true,
	shouldDirty: true
};

const VerifyCR = NiceModal.create(() => {
	const modal = useModal();
	const dispatch = useDispatch();
	const axiosPrivate = useAxiosPrivate();
	const { showSuccess, showError } = useAlert();
	const isAuthorized = useAccessControl();
	const { t } = useTranslation();
	const generalSettings = useSelector((state) => state?.user?.generalSettings);
	const [isVerifyFailed, setIsVerifyFailed] = useState(false);
	const {
		id,
		CRNumber,
		freelancerNumber,
		unifiedNumber,
		nationalId,
		invoiceType,
		BankIban,
		CRDocumentURL,
		freelanceDocument,
		civilID
	} = generalSettings;
	const schema = useMemo(() => {
		return yup.object().shape({
			invoiceType: yup.string().oneOf([ACCOUNT_TYPES.COMPANY, ACCOUNT_TYPES.FREELANCER]).required(),
			CRNumber: yup
				.string()
				.nullable()
				.when(['invoiceType', '$isVerifyFailed'], ([invoiceType, isVerifyFailed], schema) => {
					if (
						!isVerifyFailed &&
						invoiceType === ACCOUNT_TYPES.COMPANY &&
						isAuthorized('/company-settings/CommonInvoiceType')
					) {
						return schema.checkCRNumber().required();
					}
					return schema;
				}),
			unifiedNumber: yup
				.string()
				.nullable()
				.when(['invoiceType', '$isVerifyFailed'], ([invoiceType, isVerifyFailed], schema) => {
					if (!isVerifyFailed && invoiceType === ACCOUNT_TYPES.COMPANY) {
						return schema
							.matches(
								/^7\d{9}$/,
								t('validation.numberFieldLengthStartsWith', {
									maxLength: 10,
									startsWith: 7
								})
							)
							.required();
					}
					return schema;
				}),
			// Freelancer fields
			nationalId: yup
				.string()
				.when(['invoiceType', '$isVerifyFailed'], ([invoiceType, isVerifyFailed], schema) => {
					if (!isVerifyFailed && invoiceType === ACCOUNT_TYPES.FREELANCER) {
						return schema.length(10).required();
					}
					return schema.nullable();
				}),
			freelancerNumber: yup
				.string()
				.when(['invoiceType', '$isVerifyFailed'], ([invoiceType, isVerifyFailed], schema) => {
					if (!isVerifyFailed && invoiceType === ACCOUNT_TYPES.FREELANCER) {
						return schema.matches(/^FL-/, t('validation.freelancerNumber')).required();
					}
					return schema.nullable();
				}),
			// Document fields (required when verification fails)
			CRDocumentURL: yup
				.mixed()
				.nullable()
				.when(['invoiceType', '$isVerifyFailed'], ([invoiceType, isVerifyFailed], schema) => {
					if (isVerifyFailed && invoiceType === ACCOUNT_TYPES.COMPANY) {
						return schema.required();
					}
					return schema;
				}),
			civilID: yup
				.mixed()
				.nullable()
				.when(['$isVerifyFailed'], ([isVerifyFailed], schema) => {
					if (isVerifyFailed) {
						return schema.required();
					}
					return schema;
				}),
			freelanceDocument: yup
				.mixed()
				.nullable()
				.when(['invoiceType', '$isVerifyFailed'], ([invoiceType, isVerifyFailed], schema) => {
					if (isVerifyFailed && invoiceType === ACCOUNT_TYPES.FREELANCER) {
						return schema.required();
					}
					return schema;
				}),
			// Common fields
			BankIban: yup
				.string()
				.nullable()
				.when(['$isVerifyFailed'], ([isVerifyFailed], schema) =>
					isVerifyFailed ? schema : schema.required().iban()
				)
		});
	}, [isAuthorized]);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			CRNumber,
			freelancerNumber,
			unifiedNumber,
			nationalId,
			invoiceType,
			BankIban,
			CRDocumentURL,
			freelanceDocument,
			civilID
		},
		context: { isVerifyFailed }
	});

	const {
		handleSubmit,
		watch,
		setValue,
		formState: { errors, isDirty, isSubmitting, isSubmitSuccessful, isLoading: isFormLoading }
	} = formMethods;
	const isLoading = isFormLoading || isSubmitting;

	usePrompt(isDirty && !isSubmitting && !isSubmitSuccessful);
	const [autoAttemptCheckLoaded, setAutoAttemptCheckLoaded] = useState(false);

	const invoiceTypeWatch = watch('invoiceType');
	const onSubmit = async (data) => {
		try {
			const req = {
				...data,
				ID: id,
				invoiceType: data.invoiceType,
				CRNumber: data.invoiceType === ACCOUNT_TYPES.COMPANY ? data.CRNumber : null,
				unifiedNumber: data.invoiceType === ACCOUNT_TYPES.COMPANY ? data.unifiedNumber : null,
				CRDocumentURL: data.invoiceType === ACCOUNT_TYPES.COMPANY ? data.CRDocumentURL : null,
				nationalId: data.invoiceType === ACCOUNT_TYPES.FREELANCER ? data.nationalId : null,
				freelancerNumber: data.invoiceType === ACCOUNT_TYPES.FREELANCER ? data.freelancerNumber : null,
				BankIban: data.BankIban,
				civilID: data.civilID?.path || data?.civilID || null
			};
			let response;
			let documentRequest;
			if (isVerifyFailed) {
				documentRequest = {
					CRDocument:
						data.invoiceType === ACCOUNT_TYPES.COMPANY
							? data.CRDocumentURL?.path || data.CRDocumentURL
							: undefined,
					freelanceDocument:
						data.invoiceType === ACCOUNT_TYPES.FREELANCER
							? data.freelanceDocument?.path || data.freelanceDocument
							: undefined,
					civilID: data.civilID?.path || data.civilID
				};
				response = await axiosPrivate.post(COMPANY.UPLOAD_DOCUMENTS, documentRequest);
			} else {
				response = await axiosPrivate.post(COMPANY.VERIFY_CR_DOCUMENT, req);
			}
			if (response.data.docStatus === 'VERIFICATION_FAILED') {
				setIsVerifyFailed(true);
			} else if (response.data.docStatus === 'UNVERIFIED') {
				if (response.data.reason === 'ATTEMPT_COUNT_EXCEEDED') setIsVerifyFailed(true);

				showError({
					header:
						response.data.reason === 'DUPLICATE_RECORD'
							? 'cr.duplicateRecord.errorTitle'
							: response.data.reason === 'ATTEMPT_COUNT_EXCEEDED'
								? 'cr.attemptLimitExceed.title'
								: 'crUnverified',
					message:
						response.data.reason === 'DUPLICATE_RECORD'
							? 'cr.duplicateRecord.errorDescription'
							: response.data.reason === 'ATTEMPT_COUNT_EXCEEDED'
								? 'cr.attemptLimitExceed.description'
								: 'crUnverifiedDescription'
				});
			} else if (response.status === 200) {
				if (!isVerifyFailed) {
					await axiosPrivate.post(COMPANY.SET_INVOICE_TYPE, { invoiceType: data.invoiceType });
					dispatch(
						setUserGeneralSettings({
							...req,
							...data,
							FLDocStatus: req.invoiceType === ACCOUNT_TYPES.FREELANCER ? 'VERIFIED' : '',
							CRDocStatus: req.invoiceType === ACCOUNT_TYPES.COMPANY ? 'VERIFIED' : ''
						})
					);
					showSuccess();
				} else {
					await axiosPrivate.post(COMPANY.SET_INVOICE_TYPE, { invoiceType: data.invoiceType });
					dispatch(
						setUserGeneralSettings({
							...documentRequest,
							CRDocumentURL: documentRequest.CRDocument,
							invoiceType: data.invoiceType
						})
					);
					showSuccess({
						header: 'verifyDocumentUploaded',
						message: 'verifyDocumentUploadedDescription'
					});
				}
				modal.resolve(data);
				modal.remove();
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	};

	const checkAutoVerifyAttempt = async () => {
		try {
			const { data, status } = await axiosPrivate.post(COMPANY.CHECK_AUTO_VERIFY_ATTEMPT, {});
			const failReason = data?.reason;
			if (status === 200 && failReason && failReason === 'ATTEMPT_COUNT_EXCEEDED') {
				setIsVerifyFailed(true);
			}
		} catch (error) {
			AppLogger.error(error);
		} finally {
			setAutoAttemptCheckLoaded(true);
		}
	};

	useEffect(() => {
		checkAutoVerifyAttempt();
	}, []);

	const handleAccountTypeClick = (type) => {
		setValue('invoiceType', type, validateOptions);

		setValue('notVATRegistered', type !== ACCOUNT_TYPES.COMPANY, validateOptions);

		if (type === ACCOUNT_TYPES.COMPANY) {
			setValue('unifiedNumber', unifiedNumber || null, validateOptions);
			setValue('CRNumber', CRNumber || null, validateOptions);
			setValue('nationalId', null, validateOptions);
			setValue('freelancerNumber', null, validateOptions);
		} else if (type === ACCOUNT_TYPES.FREELANCER) {
			setValue('nationalId', nationalId, validateOptions);
			setValue('freelancerNumber', freelancerNumber, validateOptions);
			setValue('CRNumber', null, validateOptions);
			setValue('unifiedNumber', null, validateOptions);
		}
	};

	const radioItems = [
		{
			type: ACCOUNT_TYPES.COMPANY,
			label: 'company'
		},
		{
			type: ACCOUNT_TYPES.FREELANCER,
			label: 'freelancer'
		}
	];
	return (
		<>
			{autoAttemptCheckLoaded && (
				<S.StyledModal position="right" fullwidth maxWidth="x-small">
					<ModalTitle>
						<Typography variant="p1">accountTypeVerification</Typography>
					</ModalTitle>

					<S.Content>
						{isVerifyFailed && (
							<Alert variant="warning" content="crVerifyFailed" showIcon className="mb-5" />
						)}
						<Form formMethods={formMethods} onSubmit={handleSubmit(onSubmit)}>
							<FormItem
								label="invoiceType"
								isLoading={isLoading}
								className={'base-form-item px-0 mb-4'}
								required
								toolTip="invoiceType.description">
								<S.RadioContainer>
									{radioItems.map((item) => (
										<S.RadioItem key={item.type} onClick={() => handleAccountTypeClick(item.type)}>
											<Radio
												name="invoiceType"
												readOnly
												checked={invoiceTypeWatch === item.type}
												error={Boolean(errors?.invoiceType)}
											/>
											<S.NewAccountTypeArea>
												<Typography fontWeight="500">{item.label}</Typography>
											</S.NewAccountTypeArea>
										</S.RadioItem>
									))}
								</S.RadioContainer>
							</FormItem>
							{invoiceTypeWatch === ACCOUNT_TYPES.COMPANY && (
								<>
									{isVerifyFailed ? (
										<>
											<FormItem label="CRDocument" isLoading={isLoading} required>
												<AdvanceFileInput
													name="CRDocumentURL"
													style={{ width: '100%' }}
													btnStyle={{ width: '100%' }}
													acceptedFileType={'*'}
													setValue={(value) => {
														setValue(
															'CRDocumentURL',
															value?.path ? value : null,
															validateOptions
														);
													}}
													value={{
														name: 'CRDocumentURL',
														path: watch('CRDocumentURL') ?? null
													}}
													maxFileSize={1000000}
												/>
											</FormItem>
											<FormItem label="civilIDFrontSide" isLoading={isLoading} required>
												<AdvanceFileInput
													name="civilID"
													style={{ width: '100%' }}
													btnStyle={{ width: '100%' }}
													acceptedFileType={'*'}
													setValue={(value) => {
														setValue(
															'civilID',
															value?.path ? value : null,
															validateOptions
														);
													}}
													value={{
														name: 'civilID',
														path: watch('civilID') ?? null
													}}
													maxFileSize={1000000}
												/>
											</FormItem>
										</>
									) : (
										<>
											<FormItem label="CRNumber" isLoading={isLoading} required>
												<StringInput name="CRNumber" allowOnlyNumbers maxlength={10} />
											</FormItem>
											<FormItem label="unifiedNumber" isLoading={isLoading} required>
												<StringInput name="unifiedNumber" maxlength={10} allowOnlyNumbers />
											</FormItem>
										</>
									)}
								</>
							)}

							{invoiceTypeWatch === ACCOUNT_TYPES.FREELANCER && (
								<>
									{isVerifyFailed ? (
										<>
											<FormItem label="freelanceDocument" isLoading={isLoading} required>
												<AdvanceFileInput
													name="freelanceDocument"
													style={{ width: '100%' }}
													btnStyle={{ width: '100%' }}
													acceptedFileType={'*'}
													setValue={(value) => {
														setValue(
															'freelanceDocument',
															value?.path ? value : null,
															validateOptions
														);
													}}
													value={{
														name: 'freelanceDocument',
														path: watch('freelanceDocument') ?? null
													}}
													maxFileSize={1000000}
												/>
											</FormItem>
											<FormItem label="civilIDOrIqama" isLoading={isLoading} required>
												<AdvanceFileInput
													name="civilID"
													style={{ width: '100%' }}
													btnStyle={{ width: '100%' }}
													acceptedFileType={'*'}
													setValue={(value) => {
														setValue(
															'civilID',
															value?.path ? value : null,
															validateOptions
														);
													}}
													value={{
														name: 'civilID',
														path: watch('civilID') ?? null
													}}
													maxFileSize={1000000}
												/>
											</FormItem>
										</>
									) : (
										<>
											<FormItem label="freelancerNationalId" isLoading={isLoading} required>
												<StringInput name="nationalId" allowOnlyNumbers maxlength={10} />
											</FormItem>
											<FormItem label="freelancerNumber" isLoading={isLoading} required>
												<StringInput name="freelancerNumber" />
											</FormItem>
										</>
									)}
								</>
							)}

							{!isVerifyFailed &&
								[ACCOUNT_TYPES.COMPANY, ACCOUNT_TYPES.FREELANCER].includes(invoiceTypeWatch) && (
									<FormItem label="ibanNumber" isLoading={isLoading} required>
										<StringInput name="BankIban" />
										<Typography className="mt-2" noWrap color="var(--content-2)">
											ibanNumber.CRtooltip
										</Typography>
									</FormItem>
								)}
						</Form>
					</S.Content>

					<ModalActions>
						<Button label="cancel" variant="outline" onClick={() => modal.remove()} />
						<Button
							label="submit"
							variant="blue"
							onClick={handleSubmit(onSubmit, AppLogger.log)}
							loading={isLoading}
						/>
					</ModalActions>
				</S.StyledModal>
			)}
		</>
	);
});

export default VerifyCR;
